import { useState } from "react";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { CalendarIcon, Phone, MessageSquare } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { TREATMENT_OPTIONS } from "../data/services";
import { BOOKING_URL, PHONE_TEL, PHONE_DISPLAY } from "../data/site";
import { trackLead } from "../lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "h-12 rounded-none border-0 border-b border-neutral-300 bg-transparent px-0 text-base font-light shadow-none focus-visible:border-neutral-950 focus-visible:ring-0";
const label = "text-[10px] uppercase tracking-[0.25em] text-neutral-500";
const EMPTY = { name: "", phone: "", email: "", treatment: "", contact_method: "Call", message: "" };

export const ConsultationForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [date, setDate] = useState();
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.treatment) return toast.error("Please choose a treatment of interest.");
    setSending(true);
    try {
      await axios.post(`${API}/consultations`, { ...form, preferred_date: date ? format(date, "yyyy-MM-dd") : null });
      trackLead({ content_name: form.treatment, content_category: "consultation-form" });
      setDone(true);
      setForm(EMPTY);
      setDate(undefined);
      toast.success("Thank you. Our concierge will be in touch shortly.");
    } catch (err) {
      toast.error("We couldn't send your request. Please check your details and try again.");
    } finally {
      setSending(false);
    }
  };

  if (done)
    return (
      <div className="border border-neutral-200 bg-white p-10 md:p-14" data-testid="consultation-success">
        <p className="kicker">Request Received</p>
        <h3 className="font-display mt-4 text-4xl font-light text-neutral-950">Thank you.</h3>
        <p className="mt-4 text-base font-light leading-relaxed text-neutral-600">A member of our team will reach out personally to arrange your consultation. Prefer to choose a time now?</p>
        <div className="mt-8 flex flex-wrap gap-4">
          <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="btn-ink" data-testid="success-book-btn">Book Now</a>
          <button onClick={() => setDone(false)} className="link-under self-center" data-testid="success-new-request">Send another request</button>
        </div>
      </div>
    );

  return (
    <form onSubmit={submit} className="border border-neutral-200 bg-white p-8 md:p-14" data-testid="consultation-form">
      <div className="grid gap-8 md:grid-cols-2">
        <label className="block"><span className={label}>Full Name</span>
          <Input required value={form.name} onChange={set("name")} className={field} data-testid="form-name-input" /></label>
        <label className="block"><span className={label}>Phone</span>
          <Input required type="tel" minLength={7} value={form.phone} onChange={set("phone")} className={field} data-testid="form-phone-input" /></label>
        <label className="block"><span className={label}>Email</span>
          <Input required type="email" value={form.email} onChange={set("email")} className={field} data-testid="form-email-input" /></label>
        <div><span className={label}>Treatment of Interest</span>
          <Select value={form.treatment} onValueChange={set("treatment")}>
            <SelectTrigger className={`${field} w-full`} data-testid="form-treatment-select"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="rounded-none">
              {TREATMENT_OPTIONS.map((t) => <SelectItem key={t} value={t} data-testid={`treatment-option-${t}`}>{t}</SelectItem>)}
            </SelectContent>
          </Select></div>
        <div><span className={label}>Preferred Date</span>
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className={`${field} flex w-full items-center justify-between text-left`} data-testid="form-date-btn">
                <span className={date ? "text-neutral-950" : "text-neutral-400"}>{date ? format(date, "MMMM d, yyyy") : "Choose a date"}</span>
                <CalendarIcon size={16} strokeWidth={1.25} />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto rounded-none p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().toDateString())} data-testid="form-calendar" />
            </PopoverContent>
          </Popover></div>
        <div><span className={label}>Best Way to Reach You</span>
          <Select value={form.contact_method} onValueChange={set("contact_method")}>
            <SelectTrigger className={`${field} w-full`} data-testid="form-contact-method-select"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {["Call", "Text", "Email"].map((m) => <SelectItem key={m} value={m} data-testid={`contact-option-${m.toLowerCase()}`}>{m}</SelectItem>)}
            </SelectContent>
          </Select></div>
        <label className="block md:col-span-2"><span className={label}>Your Goals (optional)</span>
          <Textarea rows={3} value={form.message} onChange={set("message")} className={`${field} min-h-[90px] resize-none`} data-testid="form-message-input" /></label>
      </div>
      <div className="mt-10 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <button type="submit" disabled={sending} className="btn-ink disabled:opacity-60" data-testid="form-submit-btn">{sending ? "Sending…" : "Request Consultation"}</button>
        <div className="flex gap-6 text-[11px] uppercase tracking-[0.2em] text-neutral-700">
          <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2 hover:text-neutral-950" data-testid="form-call-link"><Phone size={14} strokeWidth={1.25} /> Call</a>
          <a href={`sms:${PHONE_TEL}`} className="flex items-center gap-2 hover:text-neutral-950" data-testid="form-text-link"><MessageSquare size={14} strokeWidth={1.25} /> Text {PHONE_DISPLAY}</a>
        </div>
      </div>
    </form>
  );
};
